import { useEffect, useRef, useSyncExternalStore } from 'react';
import type { Item } from './membership';

/**
 * What the page in front holds selected, for whoever else wants to act on it —
 * the palette adds it somewhere. The page owns the selection; this is only
 * what it publishes, with the call that tells it the selection was used.
 */
export interface PublishedSelection {
  items: readonly Item[];
  onUsed: () => void;
}

const NONE: PublishedSelection = { items: [], onUsed: () => {} };

let current: PublishedSelection = NONE;
const listeners = new Set<() => void>();

function set(next: PublishedSelection): void {
  current = next;
  for (const listener of listeners) listener();
}

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => void listeners.delete(listener);
};

export function useSelection(): PublishedSelection {
  return useSyncExternalStore(subscribe, () => current, () => NONE);
}

/** Publishes [items] while the page holds them, and withdraws them on the way out. */
export function usePublishSelection(items: readonly Item[], onUsed: () => void): void {
  const used = useRef(onUsed);
  used.current = onUsed;
  useEffect(() => {
    if (items.length === 0) return;
    set({ items, onUsed: () => used.current() });
    return () => set(NONE);
  }, [items]);
}
